import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../firebase/config';
import { useAuth } from './AuthContext';

// Cart Item Interface
export interface CartItem {
  id: string;
  productId: string;
  vendorId: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
  total: number;
  shippingCost: number;
  sku?: string;
  category?: string;
  isDigital?: boolean;
}

// Cart Context Interface
interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  cartTotal: number;
  shippingTotal: number;
  grandTotal: number;
  isCartOpen: boolean;
  isLoading: boolean;
  addToCart: (item: Omit<CartItem, 'id' | 'total'>) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

// Create Context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Cart Provider Props
interface CartProviderProps {
  children: ReactNode;
}

// Cart Provider Component
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();

  // Calculate cart count, total, and shipping
  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const cartTotal = cartItems.reduce((total, item) => total + item.total, 0);
  const shippingTotal = cartItems.reduce((total, item) => total + (Number(item.shippingCost) || 0), 0);
  const grandTotal = cartTotal + shippingTotal;

  // Load cart from Firestore when user authenticates
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setCartItems([]);
      return;
    }
    const loadCart = async () => {
      const { data, error } = await supabase
        .from('carts')
        .select('items')
        .eq('user_id', user.uid)
        .maybeSingle();

      if (error) {
        console.error('Error loading cart:', error);
        return;
      }

      if (!data) {
        await supabase.from('carts').insert({
          user_id: user.uid,
          user_email: user.email,
          items: [],
        });
        setCartItems([]);
        return;
      }

      setCartItems((data.items as CartItem[]) || []);
    };

    loadCart();
  }, [user, isAuthenticated]);

  // Add item to cart
  const addToCart = async (item: Omit<CartItem, 'id' | 'total'>) => {
    if (!isAuthenticated || !user) {
      throw new Error('User must be authenticated to add items to cart');
    }

    setIsLoading(true);
    try {
      const { data: cartDoc, error } = await supabase
        .from('carts')
        .select('items')
        .eq('user_id', user.uid)
        .maybeSingle();
      if (error) throw error;
      
      const newItem: CartItem = {
        ...item,
        id: `${item.productId}-${item.vendorId}`,
        total: item.price * item.quantity
      };

      if (cartDoc) {
          const existingItems = (cartDoc.items as CartItem[]) || [];
          const existingItemIndex = existingItems.findIndex((existing: CartItem) => existing.id === newItem.id);
          
          let updatedItems;
          if (existingItemIndex >= 0) {
            // Update existing item quantity
            updatedItems = existingItems.map((existing: CartItem, index: number) => 
              index === existingItemIndex 
                ? { ...existing, quantity: existing.quantity + newItem.quantity, total: existing.price * (existing.quantity + newItem.quantity) }
                : existing
            );
          } else {
            // Add new item
            updatedItems = [...existingItems, newItem];
          }

          const { error: updateError } = await supabase
            .from('carts')
            .update({ items: updatedItems, updated_at: new Date().toISOString() })
            .eq('user_id', user.uid);
          if (updateError) throw updateError;
          setCartItems(updatedItems);
        } else {
          const { error: insertError } = await supabase.from('carts').insert({
            user_id: user.uid,
            user_email: user.email,
            items: [newItem],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
          if (insertError) throw insertError;
          setCartItems([newItem]);
        }
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update item quantity
  const updateQuantity = async (itemId: string, quantity: number) => {
    if (!isAuthenticated || !user) {
      throw new Error('User must be authenticated to update cart');
    }

    if (quantity <= 0) {
      await removeFromCart(itemId);
      return;
    }

    setIsLoading(true);
    try {
      const { data: cartDoc, error } = await supabase
        .from('carts')
        .select('items')
        .eq('user_id', user.uid)
        .maybeSingle();
      if (error) throw error;
      
      if (cartDoc) {
        const existingItems = (cartDoc.items as CartItem[]) || [];
        const updatedItems = existingItems.map((item: CartItem) => 
          item.id === itemId 
            ? { ...item, quantity, total: item.price * quantity }
            : item
        );

        const { error: updateError } = await supabase
          .from('carts')
          .update({ items: updatedItems, updated_at: new Date().toISOString() })
          .eq('user_id', user.uid);
        if (updateError) throw updateError;
        setCartItems(updatedItems);
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Remove item from cart
  const removeFromCart = async (itemId: string) => {
    if (!isAuthenticated || !user) {
      throw new Error('User must be authenticated to remove items from cart');
    }

    setIsLoading(true);
    try {
      const { data: cartDoc, error } = await supabase
        .from('carts')
        .select('items')
        .eq('user_id', user.uid)
        .maybeSingle();
      if (error) throw error;
      
      if (cartDoc) {
        const existingItems = (cartDoc.items as CartItem[]) || [];
        const updatedItems = existingItems.filter((item: CartItem) => item.id !== itemId);

        const { error: updateError } = await supabase
          .from('carts')
          .update({ items: updatedItems, updated_at: new Date().toISOString() })
          .eq('user_id', user.uid);
        if (updateError) throw updateError;
        setCartItems(updatedItems);
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (!isAuthenticated || !user) {
      throw new Error('User must be authenticated to clear cart');
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('carts')
        .update({ items: [], updated_at: new Date().toISOString() })
        .eq('user_id', user.uid);
      if (error) throw error;
      setCartItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Cart popup controls
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(!isCartOpen);

  const value: CartContextType = {
    cartItems,
    cartCount,
    cartTotal,
    shippingTotal,
    grandTotal,
    isCartOpen,
    isLoading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    openCart,
    closeCart,
    toggleCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 